// NOTICE: KAPTION-INTEGRATION
// Composable for managing privacy mode state persisted to localStorage, scoped per account
// Two independent global modes:
//   1. useKaptionPrivacyMode() — blur conversation cards in the chat list
//   2. useKaptionMessagePrivacyMode() — blur messages inside conversations
import { ref, computed, watch } from 'vue';
import { useAccount } from './useAccount';

// Shared module-level state for chat list privacy (KaptionActionPill + ChatList)
const privacyEnabled = ref(false);
// eslint-disable-next-line no-underscore-dangle
let _initializedForAccount = null;

// Shared module-level state for message privacy (KaptionAIFeaturesPill + MessagesView)
const messagePrivacyEnabled = ref(false);
// eslint-disable-next-line no-underscore-dangle
let _msgInitializedForAccount = null;

/**
 * Global privacy mode (blur all conversation cards in the chat list)
 */
export function useKaptionPrivacyMode() {
  let accountId = ref(null);
  try {
    // useAccount requires vue-router and vuex, which may not be available in stories
    const { accountId: acctId } = useAccount();
    accountId.value = acctId.value;
  } catch {
    // Silent fallback for Histoire/test environments
  }

  const storageKey = computed(() => {
    if (!accountId.value) return null;
    return `kaption-privacy-mode-${accountId.value}`;
  });

  const loadFromStorage = () => {
    if (storageKey.value) {
      // Avoid redundant loads for the same account
      if (_initializedForAccount === storageKey.value) return;
      _initializedForAccount = storageKey.value;
      try {
        const stored = localStorage.getItem(storageKey.value);
        privacyEnabled.value = stored ? JSON.parse(stored) : false;
      } catch {
        privacyEnabled.value = false;
      }
    }
  };

  const setPrivacy = enabled => {
    if (storageKey.value) {
      privacyEnabled.value = enabled;
      try {
        localStorage.setItem(storageKey.value, JSON.stringify(enabled));
      } catch {
        // Silent failure
      }
    }
  };

  const togglePrivacy = () => {
    setPrivacy(!privacyEnabled.value);
  };

  watch([storageKey], loadFromStorage, {
    immediate: true,
    flush: 'sync',
  });

  return {
    privacyEnabled,
    togglePrivacy,
    setPrivacy,
  };
}

/**
 * Global message privacy mode (blur messages inside all conversations)
 * Independent from useKaptionPrivacyMode which blurs the chat list.
 */
export function useKaptionMessagePrivacyMode() {
  let accountId = ref(null);
  try {
    const { accountId: acctId } = useAccount();
    accountId.value = acctId.value;
  } catch {
    // Silent fallback for Histoire/test environments
  }

  const storageKey = computed(() => {
    if (!accountId.value) return null;
    return `kaption-message-privacy-mode-${accountId.value}`;
  });

  const loadFromStorage = () => {
    if (storageKey.value) {
      if (_msgInitializedForAccount === storageKey.value) return;
      _msgInitializedForAccount = storageKey.value;
      try {
        const stored = localStorage.getItem(storageKey.value);
        messagePrivacyEnabled.value = stored ? JSON.parse(stored) : false;
      } catch {
        messagePrivacyEnabled.value = false;
      }
    }
  };

  const setPrivacy = enabled => {
    if (storageKey.value) {
      messagePrivacyEnabled.value = enabled;
      try {
        localStorage.setItem(storageKey.value, JSON.stringify(enabled));
      } catch {
        // Silent failure
      }
    }
  };

  const togglePrivacy = () => {
    setPrivacy(!messagePrivacyEnabled.value);
  };

  watch([storageKey], loadFromStorage, {
    immediate: true,
    flush: 'sync',
  });

  return {
    privacyEnabled: messagePrivacyEnabled,
    togglePrivacy,
    setPrivacy,
  };
}
